import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { DownloadIcon, YaySmallIcon } from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import NavigatorButton from 'components/admin/PlaybookSteps/NavigatorButton'
import {
  EstimatedTime,
  GetStartedButton,
  PageContent,
  PageNavigator,
  PlaybookStepsWrapper,
  RightContent,
  StepDropdown,
  StepNumber,
  StyledHr,
  StyledLinkDropdown,
  SubTitle,
  SummaryLink,
  TextContent,
  Title,
  TopWrapper,
} from './component.styles'
import {
  DROPDOWN_DEFAULT_LABEL,
  DROPDOWN_NAME,
  GET_STARTED_BUTTON_TEXT,
  GET_STARTED_BUTTON_TEST_ID,
  GOALS_TEXT,
  HOST_TEXT,
  MARK_COMPLETE_BUTTON_TEXT,
  PAGE_CONTENT_TEST_ID,
  PURPOSE_TEXT,
  RESUME_BUTTON_TEXT,
  SUMMARY_TEXT,
} from './constants'
import { dropdownOptions, modalMap, stepDataMap } from './data'

const PlaybookSteps = ({
  familyLetterSent,
  goalsAreCompleted,
  goalsIsInDraft,
  implementationApproachIsCompleted,
  kickoffHosted,
  kickoffPrepared,
  navigationEvent,
  linkDropdownDisplayTitle,
  linkMenu,
  programStartDateIsCompleted,
  purposeIsCompleted,
  purposeIsInDraft,
  siteId,
  userInvitationMethod,
}) => {
  const [activeStep, setActiveStep] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [ModalComponent, setModalComponent] = useState(null)
  const [selectedDropdownOption, setSelectedDropdownOption] = useState(
    dropdownOptions[0],
  )
  const [StepIcon, setStepIcon] = useState(null)
  const [showSummary, setShowSummary] = useState(false)

  const defaultLabel = selectedDropdownOption
    ? selectedDropdownOption.name
    : DROPDOWN_DEFAULT_LABEL

  const dropdownLinkNavigation = link => {
    const { download = null, openSameTab, url: optionUrlPath } = link || {}

    if (download) {
      navigationEvent(
        download?.file?.file?.url,
        NavigationHelper.types.DOWNLOAD_LINK,
      )
    }

    const type = openSameTab
      ? NavigationHelper.types.PUSH
      : NavigationHelper.types.NEW_TAB
    navigationEvent(optionUrlPath, type)
  }

  const handleActiveStep = () => {
    let newStep = 1

    if (selectedDropdownOption?.key === 'plan') {
      if (implementationApproachIsCompleted) {
        if (!purposeIsCompleted) {
          newStep = 2
        } else if (!goalsAreCompleted) {
          newStep = 3
        } else if (!programStartDateIsCompleted) {
          newStep = 4
        }
      }
    }

    if (selectedDropdownOption?.key === 'launch') {
      if (userInvitationMethod) {
        if (!kickoffPrepared) {
          newStep = 2
        } else if (!kickoffHosted) {
          newStep = 3
        } else if (!familyLetterSent) {
          newStep = 4
        }
      }
    }

    if (newStep !== 0) {
      setStepIcon(
        () => stepDataMap?.[selectedDropdownOption?.key]?.[newStep]?.Icon,
      )
    } else {
      setStepIcon(() => stepDataMap?.[selectedDropdownOption?.key]?.[1]?.Icon)
    }

    setActiveStep(newStep)
  }

  const launchModal = modalName => {
    const Modal = modalMap[modalName]?.component
    if (Modal) {
      setModalComponent(() => Modal)
      setIsModalOpen(true)
    }
  }

  const onChangeDropdown = e => {
    const optionToSelect = dropdownOptions.find(option => option.key === e.key)
    setSelectedDropdownOption(optionToSelect)
    handleActiveStep()
  }

  const setButtonText = modalName => {
    let buttonText = GET_STARTED_BUTTON_TEXT

    if (modalName === GOALS_TEXT && goalsIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === PURPOSE_TEXT && purposeIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === HOST_TEXT) {
      buttonText = MARK_COMPLETE_BUTTON_TEXT
    }

    return buttonText
  }

  useEffect(() => {
    if (
      selectedDropdownOption?.key === 'plan' &&
      goalsAreCompleted &&
      implementationApproachIsCompleted &&
      programStartDateIsCompleted &&
      purposeIsCompleted
    ) {
      setShowSummary(true)
      setActiveStep(0)
    } else if (
      selectedDropdownOption?.key === 'launch' &&
      familyLetterSent &&
      kickoffPrepared &&
      kickoffHosted &&
      userInvitationMethod
    ) {
      setShowSummary(true)
      setActiveStep(0)
    } else {
      setShowSummary(false)
      handleActiveStep()
    }
  }, [
    activeStep,
    familyLetterSent,
    goalsAreCompleted,
    implementationApproachIsCompleted,
    kickoffHosted,
    kickoffPrepared,
    programStartDateIsCompleted,
    purposeIsCompleted,
    selectedDropdownOption,
    showSummary,
    userInvitationMethod,
  ])

  return (
    <PlaybookStepsWrapper>
      <TopWrapper>
        <StepDropdown
          dataTestId="playbook-steps-dropdown"
          defaultLabel={defaultLabel}
          keyProp="key"
          labelProp="name"
          name={DROPDOWN_NAME}
          onChange={onChangeDropdown}
          options={dropdownOptions}
          selectOption={selectedDropdownOption}
          value={selectedDropdownOption}
        />
      </TopWrapper>
      <PageContent dataTestId={PAGE_CONTENT_TEST_ID}>
        {!showSummary && (
          <Fragment key="not-complete-step">
            {!!StepIcon && <StepIcon id="step-icon" />}
            <TextContent>
              <StepNumber>{`STEP ${
                stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]?.number
              }`}</StepNumber>
              <Title>
                {
                  stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                    ?.title
                }
              </Title>
              <EstimatedTime>
                {`( ${
                  stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                    ?.estimatedTime
                } )`}
              </EstimatedTime>
            </TextContent>
            <GetStartedButton
              dataTestId={GET_STARTED_BUTTON_TEST_ID}
              onClick={() =>
                launchModal(
                  stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                    ?.modalName,
                )
              }
            >
              {setButtonText(
                stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                  ?.modalName,
              )}
            </GetStartedButton>
          </Fragment>
        )}
        {showSummary && selectedDropdownOption?.key === 'plan' && (
          <Fragment key="complete-step">
            <YaySmallIcon id="step-icon" />
            <TextContent>
              <Title>You're on your way!</Title>
              <SubTitle>
                Download and complete the customizable action plan and prepare
                for launch.
              </SubTitle>
            </TextContent>
            <RightContent>
              {linkMenu && (
                <StyledLinkDropdown
                  defaultLabel={linkDropdownDisplayTitle}
                  dropdownButtonIcon={<DownloadIcon />}
                  key="link-dropdown"
                  keyProp="id"
                  labelProp="buttonText"
                  onChange={link => dropdownLinkNavigation(link)}
                  options={linkMenu}
                  shouldHideSelectedIcon={true}
                />
              )}
              <SummaryLink onClick={() => launchModal(SUMMARY_TEXT)}>
                {SUMMARY_TEXT}
              </SummaryLink>
            </RightContent>
          </Fragment>
        )}
        {showSummary && selectedDropdownOption?.key === 'launch' && (
          <Fragment key="complete-step">
            <YaySmallIcon id="step-icon" />
            <TextContent>
              <Title>You've launched the program!</Title>
              <SubTitle>
                Use the Launch Snapshot to make sure teachers are getting
                started.
              </SubTitle>
            </TextContent>
          </Fragment>
        )}
      </PageContent>
      <PageNavigator>
        <NavigatorButton
          isActive={activeStep === 1}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? implementationApproachIsCompleted
              : !!userInvitationMethod
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[1]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[1]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[1]?.label}
        </NavigatorButton>
        <StyledHr />
        <NavigatorButton
          isActive={activeStep === 2}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? purposeIsCompleted
              : !!kickoffPrepared
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[2]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[2]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[2]?.label}
        </NavigatorButton>
        <StyledHr />
        <NavigatorButton
          isActive={activeStep === 3}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? goalsAreCompleted
              : !!kickoffHosted
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[3]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[3]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[3]?.label}
        </NavigatorButton>
        <StyledHr />
        <NavigatorButton
          isActive={activeStep === 4}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? programStartDateIsCompleted
              : !!familyLetterSent
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[4]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[4]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[4]?.label}
        </NavigatorButton>
      </PageNavigator>
      {isModalOpen && ModalComponent && (
        <ModalComponent
          handleClose={() => setIsModalOpen(false)}
          siteId={siteId}
        />
      )}
    </PlaybookStepsWrapper>
  )
}

PlaybookSteps.propTypes = {
  familyLetterSent: PropTypes.bool,
  goalsAreCompleted: PropTypes.bool,
  goalsIsInDraft: PropTypes.bool,
  implementationApproachIsCompleted: PropTypes.bool,
  kickoffHosted: PropTypes.bool,
  kickoffPrepared: PropTypes.bool,
  linkDropdownDisplayTitle: PropTypes.string,
  linkMenu: PropTypes.array,
  navigationEvent: PropTypes.func,
  programStartDateIsCompleted: PropTypes.bool,
  purposeIsCompleted: PropTypes.bool,
  purposeIsInDraft: PropTypes.bool,
  siteId: PropTypes.number,
  userInvitationMethod: PropTypes.string,
}

export default PlaybookSteps
